import React from 'react';
import {
    Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Progress,
} from 'reactstrap';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch, faDownload, faTasks } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import CommonContext, { ApiRoutes, ApplicationPermissions } from '../Common';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    LinkCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
    getServerSideRowRequest,
    EmployeeAccountStatusRenderer,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    PageHeading, PageWrap, SmallButton, toasty,
} from '../common/forms/FormElements';
import TimesheetDetails from '../timesheet/TimesheetDetails';
import { Alert } from '../common/forms/ModalForm';
import { Modal as Prompt } from '../common/Modal';
import HoursReport from './HoursReport';

const transformRowData = (data) => {
    data.rows.forEach((row) => {
        if (row.isBreak && row.isPaidBreak) {
            // Combine these columns to save space.
            row.quantityDisplay = `${row.quantity} ${row.unitsName} P`;
        } else if (row.isBreak && !row.isPaidBreak) {
            // Combine these columns to save space.
            row.quantityDisplay = `${row.quantity} ${row.unitsName} UP`;
        } else {
            // Combine these columns to save space.
            row.quantityDisplay = `${row.quantity} ${row.unitsName}`;
        }

        // Placeholder column so we can have it be the last checkable thing on the right.
        // Otherwise, we could set checkboxSelection = true for an existing column.
        row.selectionPlaceholder = '';
    });
    return { ...data };
};

// Added by MFN.  May need to be removed/altered.
class ChargeViewIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.timesheetDetailsRef = React.createRef();

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            showHoursReport: false,
            isGeneratingHoursReport: false,
            timesheetDetailsOpen: false,
        };

        this.onRowSelected = this.onRowSelected.bind(this);
        this.generateHoursReportRef = React.createRef();
    }

    componentDidMount() {
        return this.populateState();
    }

    async onAcceptRejectButtonClick(isAccept) {
        // TODO: Anything needed with the response.
        const url = ApiRoutes.chargeManagement.update(isAccept);
        await httpClient.post(url, this.state.rowsSelected);
    }

    // Before allowing the excel export, check specified date range, if any, and try to
    // protect against abnormally large datasets.  We don't use the way in DataGridToolbar
    // so we can launch a popup beforehand.
    onExportToExcelClicked = async () => {
        const { gridOptions } = { ...this.state };
        const req = getServerSideRowRequest(gridOptions);

        const from = req?.filterModel['Date']?.dateFrom;
        const to = req?.filterModel['Date']?.dateTo;

        if (!from || !to) {
            toasty.error('Please specify a date range when exporting Charge View search results to excel.');
        } else {
            const daySpan = moment(new Date(to)).diff(moment(new Date(from)), 'days');

            if (daySpan > 30) {
                this.showConfirmExportLargeDataset();
                return false;
            }

            await this.exportToExcel();
        }
    };

    onBillingReportClicked = async () => {
        const { gridOptions } = { ...this.state };
        const req = getServerSideRowRequest(gridOptions);

        const from = req?.filterModel['Date']?.dateFrom;
        const to = req?.filterModel['Date']?.dateTo;

        if (!from || !to) {
            toasty.error('Please specify a date range when exporting Charge View search results to excel.');
        } else {
            const daySpan = moment(new Date(to)).diff(moment(new Date(from)), 'days');

            if (daySpan > 30) {
                this.showConfirmExportLargeBillingReport();
                return false;
            }

            await this.generateBillingReport();
        }
    };

    generateBillingReport = async () => {
        this.setState({ exporting: true });
        const { gridOptions } = { ...this.state };
        const req = getServerSideRowRequest(gridOptions);
        const fileName = `BillingReport_${moment().format('YYYYMMDD_hhmmss')}.xlsx`;
        try {
            await httpClient.downloadFile(ApiRoutes.chargeView.billingReportExport(), req, fileName);
        } catch (err) {
            toasty.error(`Error exporting data: ${err}`);
        } finally {
            this.setState({ exporting: false });
        }
    };

    // https://stackoverflow.com/questions/44263350/count-number-of-selected-rows-in-ag-grid
    // Possibly use lodash dequeue?
    onRowSelected(e) {
        const rs = e.api.getSelectedRows();

        this.setState({
            rowsSelected: rs,
        });
    }

    onTimesheetDetailsClosed = async () => {
        await this.context.setFormOpened(false);
        this.setState({ selectedRow: null });
        this.state.gridOptions.refresh();
    };

    getColumnDefs({
        dispatchLocations, timesheetDetailStatuses, payrollStatuses, yesNo,
    }) {
        // https://www.ag-grid.com/documentation/javascript/column-definitions/

        const dispatchFilterParams = {
            suppressFilterButton: true,
            labelText: 'Filter by Dispatch',
            options: dispatchLocations,
            optionsLabel: 'label',
            optionsValue: 'value',
        };
        const timesheetStatusFilterParams = {
            suppressFilterButton: true,
            options: timesheetDetailStatuses,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: null,
        };
        const yesNoFilterParams = {
            suppressFilterButton: true,
            options: yesNo,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: null,
        };
        const payrollStatusFilterParams = {
            suppressFilterButton: true,
            options: payrollStatuses,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: null,
        };

        const defs = [
            {
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                hide: true,
                flex: 1,
                maxWidth: 35,
                cellRenderer: this.indexCellRenderer,
            },
            {
                colId: 'Date',
                headerName: 'Date',
                field: 'date',
                sortable: true,
                flex: 1.5,
                sort: { direction: 'asc', priority: 0 },
                filter: 'agDateColumnFilter',
                filterParams: DateFilterDefaults,
                // floatingFilterComponentParams: {
                //    suppressFilterButton: true,
                // }
            },
            {
                colId: 'CustomerName',
                headerName: 'Customer Name',
                field: 'customerName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                valueGetter: (row) => {
                    if (row.data) {
                        const name = `${row.data.customerName}${(row.data.subcontractorName ? ` - ${row.data.subcontractorName}` : '')}`;
                        return name;
                    }

                    return '';
                },
            },
            {
                colId: 'TimesheetNumber',
                headerName: 'Timesheet #',
                field: 'timesheetNumber',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: (params) => {
                    if (params.data) {
                        if (params.data.timesheetId) {
                            return (
                                <Button
                                    color="link"
                                    className="site-link"
                                    disabled={!!this.context.formIsOpen}
                                    title="OpenTimesheet"
                                    onClick={() => {
                                        this.openTimesheetDetails(params.data.timesheetId);
                                    }}
                                >
                                    {params.data.timesheetNumber}
                                    {!!(params.data.timesheetNumber && params.data.isSelfDispatching) && (
                                        <Badge
                                            color="info"
                                            className="ml-2"
                                            style={{ fontSize: '1em', marginTop: '-2px' }}
                                        >
                                            {params.data.isNonFlagging ? 'NF' : 'SD'}
                                        </Badge>
                                    )}
                                </Button>
                            );
                        }

                        return params.data.timesheetNumber;
                    }

                    return null;
                },
            },
            {
                colId: 'WorkOrderNumber',
                headerName: 'Work Order',
                field: 'workOrderNumber',
                sortable: true,
                flex: 1.5,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'DispatchOfficeId',
                headerName: 'Dispatching',
                sortable: true,
                flex: 1.5,
                field: 'dispatchOfficeName',
                tooltipField: 'dispatchOfficeName',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: { ...dispatchFilterParams },
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: { ...dispatchFilterParams },
            },
            {
                colId: 'ResourceName',
                headerName: 'Resource',
                field: 'resourceName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRenderer: 'employeeAccountStatusRenderer',
                cellRendererParams: {
                    nameField: 'resourceName',
                },
            },
            {
                colId: 'ChargeTypeName',
                headerName: 'Charge Type',
                field: 'chargeTypeName',
                sortable: true,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Start',
                headerName: 'Start',
                field: 'start',
                sortable: false,
                maxWidth: 100,

            },
            {
                colId: 'End',
                headerName: 'Stop',
                field: 'end',
                sortable: false,
                maxWidth: 100,

            },
            {
                colId: 'QuantityDisplay',
                headerName: 'Quantity',
                field: 'quantityDisplay',
                type: 'rightAligned',
                sortable: false,
                flex: 1.5,
                cellStyle: (q) => {
                    if ((q.data ?? {}).isBreak) {
                        if (q.data.isPaidBreak) {
                            return { color: 'blue' };
                        }

                        return { color: 'red' };
                    }

                    return {};
                },
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'Billable',
                headerName: 'Billable',
                field: 'billable',
                sortable: false,
                flex: 1,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
            },
            {
                colId: 'Status',
                headerName: 'Status',
                field: 'status',
                sortable: true,
                flex: 2,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: timesheetStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: timesheetStatusFilterParams,
            },
            {
                colId: 'NotApplicable',
                headerName: 'Applicable?',
                field: 'notApplicable',
                flex: 1,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: {
                    suppressFilterButton: true,
                    options: [
                        { label: 'Yes', value: 'Yes' },
                        { label: 'No', value: 'No' },
                    ],
                    optionsLabel: 'label',
                    optionsValue: 'value',
                    initialFilterValue: 'Yes',
                },
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                    options: [
                        { label: 'Yes', value: 'Yes' },
                        { label: 'No', value: 'No' },
                    ],
                    optionsLabel: 'label',
                    optionsValue: 'value',
                },
                valueFormatter: (params) => params.value ? 'No' : 'Yes',
            },
            {
                colId: 'PayrollStatusId',
                headerName: 'Payroll Status',
                field: 'payrollStatus',
                sortable: true,
                flex: 1.5,
                hide: !this.context.tenant.tenantSettings.enablePayrollStatusInChargeView,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: payrollStatusFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: payrollStatusFilterParams,
            },
        ];

        return defs;
    }

    exportToExcel = async () => {
        this.setState({ exporting: true });
        const { gridOptions } = { ...this.state };
        const req = getServerSideRowRequest(gridOptions);
        const fileName = `ChargeViewSearch_${moment().format('YYYYMMDD_hhmmss')}.xlsx`;
        try {
            await httpClient.downloadFile(ApiRoutes.chargeView.excelExport(), req, fileName);
        } catch (err) {
            toasty.error(`Error exporting data: ${err}`);
        } finally {
            this.setState({ exporting: false });
        }
    };

    // TODO: Replace exportToExcel above with this implementation when task queue function tested
    // exportToExcel = async () => {
    //   this.setState({ showConfirmExportLargeDataset: false });
    //   const { gridOptions } = { ...this.state };
    //   const req = getServerSideRowRequest(gridOptions);
    //   try {
    //     const response = await httpClient.post(ApiRoutes.chargeView.excelExport(), req);
    //     if (response.data?.warning) {
    //       toasty.warning('Warning.', response.message,
    //         { autoClose: false, closeOnClick: true },
    //       )
    //     } else {
    //       toasty.success('Export task created.', response.message,
    //         { autoClose: false, closeOnClick: true },
    //       )
    //     }
    //   } catch (err) {
    //     toasty.error(`Error exporting data: ${err}`);
    //   } finally {
    //     this.setState({ exporting: false });
    //   }
    // };

    openTimesheetDetails = async (id) => {
        if (id) {
            await this.context.setFormOpened(true);
            this.timesheetDetailsRef.current.open(id);
        }
    };

    populateState = async () => {
        const dispatchLocations = await httpClient.js(ApiRoutes.typeAheads.dispatchLocations());
        const timesheetDetailStatuses = await httpClient.js(ApiRoutes.typeAheads.timesheetStatuses());
        const payrollStatuses = await httpClient.js(ApiRoutes.typeAheads.payrollStatuses());

        const yesNo = [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ];

        const gridOptions = createGridOptions(this);

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
            employeeAccountStatusRenderer: EmployeeAccountStatusRenderer,
        };

        // https://www.ag-grid.com/documentation/javascript/row-selection/#checkbox-selection
        // One column must have "checkboxSelection" set to true.
        // Note: headerCheckboxSelection not available with infinite scroll.
        // gridOptions.rowSelection = "multiple";

        gridOptions.postProcessData = transformRowData;
        gridOptions.onRowSelected = this.onRowSelected;

        gridOptions.columnDefs = this.getColumnDefs({
            dispatchLocations, timesheetDetailStatuses, payrollStatuses, yesNo,
        });

        const context = this;

        // On the grid's first load, we want to apply the fsilters immediately from the state population,
        // before the API call is made.
        gridOptions.onGridReady = (params) => {
            // Calc past 61 days
            const start = moment(moment().subtract(61, 'days')).format(
                'YYYY-MM-DD',
            );
            const end = moment(moment().subtract(1, 'days')).format(
                'YYYY-MM-DD',
            );

            const filterInstance = gridOptions.api.getFilterInstance('Date');
            filterInstance.setModel({
                type: 'inRange',
                dateFrom: start,
                dateTo: end,
            });
            gridOptions.api.onFilterChanged();

            params.api.setDatasource(context.state.dataSource);
            context.setState({ gridApi: params.api });
        };

        const dataSource = createDataSource(ApiRoutes.chargeView.search(), gridOptions);

        this.setState({
            dispatchLocations,
            timesheetDetailStatuses,
            payrollStatuses,
            yesNo,
            loading: false,
            gridOptions,
            dataSource,
        });
    };

    showConfirmExportLargeDataset = () => {
        Prompt.show({
            title: 'Warning: Large Dataset Request',
            onSubmit: () => this.exportToExcel(),
            SaveButtonProps: {
                label: 'Yes, continue',
                savingLabel: 'Exporting',
                icon: faDownload,
            },
            body: () => (
                <>
                    You are requesting a large amount of Charge View data (over 30 days).
                    This operation could potentially take a long time.
                    It is recommended that you set an appropriate date range to reduce the size of the request.
                    <br />
                    <br />
                    Proceed?
                </>
            ),
        });
    };

    showConfirmExportLargeBillingReport = () => {
        Prompt.show({
            title: 'Warning: Large Dataset Request',
            onSubmit: () => this.generateBillingReport(),
            SaveButtonProps: {
                label: 'Yes, continue',
                savingLabel: 'Exporting',
                icon: faDownload,
            },
            body: () => (
                <>
                    You are requesting a large amount of Charge View data (over 30 days).
                    This operation could potentially take a long time.
                    It is recommended that you set an appropriate date range to reduce the size of the request.
                    <br />
                    <br />
                    Proceed?
                </>
            ),
        });
    };

    render() {
        if (this.state.loading) return (<Progress />);

        const {
            exporting, rowData, gridOptions, showHoursReport, isGeneratingHoursReport,
        } = this.state;

        const { hasHoursReport } = this.context.tenant.tenantSettings;
        const canViewBillingReport = !!this.context.checkUserHasPermission(ApplicationPermissions.billing_report_view);

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faTasks}
                        className="mr-2 text-muted"
                    />
                    <span>View Timecharges</span>
                </PageHeading>
                <DataGridToolbar
                    entity="timechargeDetails"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    hideAdd
                    hideExcelButton
                >
                    {hasHoursReport

                        && (
                            <SmallButton
                                disabled={!!this.state.saving}
                                onClick={() => this.setState({ showHoursReport: true })}
                            >
                                <i className="fa fa-file-invoice fa-lg" />
                                <span className="ml-2 small-viewport-hide">Hours Report</span>
                            </SmallButton>
                        )}

                    <SmallButton

                        disabled={!!this.state.saving}
                        onClick={this.onExportToExcelClicked}
                    >
                        <i className="fa fa-file-excel fa-lg" />
                        <span className="ml-2 small-viewport-hide">Export to Excel</span>
                    </SmallButton>
                    {canViewBillingReport && (
                        <SmallButton
                            disabled={!!this.state.saving}
                            onClick={this.onBillingReportClicked}
                        >
                            <i className="fa fa-file-excel fa-lg" />
                            <span className="ml-2 small-viewport-hide">Billing Report</span>
                        </SmallButton>
                    )}
                </DataGridToolbar>
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    gridStatus={this.state.gridStatus}
                />

                <TimesheetDetails
                    ref={this.timesheetDetailsRef}
                    show={this.state.timesheetDetailsOpen}
                    toggleShow={(open) => this.setState({ timesheetDetailsOpen: open })}
                    onClose={this.onTimesheetDetailsClosed}
                    isBilling={false}
                    isPayroll={false}
                />

                {hasHoursReport

                    && (
                        <Modal
                            size="lg"
                            backdrop="static"
                            keyboard={false}
                            isOpen={showHoursReport}
                        >
                            <ModalHeader>Hours Report</ModalHeader>
                            <ModalBody>
                                <HoursReport
                                    generateRef={this.generateHoursReportRef}
                                    onGenerateStatusChange={(isGen) => this.setState({ isGeneratingHoursReport: isGen })}
                                    onGenerateError={(err) => toasty.error(`Error generating report: ${err}`)}
                                />
                            </ModalBody>
                            <ModalFooter>
                                <Spinner
                                    type="border"
                                    tag="span"
                                    size="sm"
                                    aria-hidden="true"
                                    hidden={!isGeneratingHoursReport}
                                />
                                <Button
                                    className="d-flex flex-row flex-nowrap align-items-center"
                                    color="primary"
                                    innerRef={this.generateHoursReportRef}
                                >
                                    Generate Report

                                </Button>
                                <Button
                                    className="d-flex flex-row flex-nowrap align-items-center"
                                    color="secondary"
                                    onClick={() => this.setState({ showHoursReport: false })}
                                >
                                    Cancel
                                </Button>
                            </ModalFooter>
                        </Modal>
                    )}

                <Alert isOpen={exporting}>
                    <FontAwesomeIcon
                        icon={faCircleNotch}
                        className="fa-spin mr-2"
                        size="sm"
                    />
                    &nbsp;Exporting...
                </Alert>
            </PageWrap>
        );
    }
}

export default ChargeViewIndex;
